import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

// Rough CO2 intensity in kg CO2e per 10.000 EUR investment per year (illustrative averages)
const INTENSITIES = {
  classic: 2400, // MSCI World / classic broad market
  esg: 1300,     // ESG screened
  paris: 700,    // Paris-aligned benchmark
  impact: 350,   // dedicated impact / clean-energy
};

export default function CO2Calculator() {
  const [amount, setAmount] = useState(25000);
  const [mix, setMix] = useState({ classic: 20, esg: 50, paris: 20, impact: 10 });

  const total = mix.classic + mix.esg + mix.paris + mix.impact;

  const setPart = (k, v) => {
    setMix((m) => ({ ...m, [k]: v }));
  };

  const chartData = useMemo(() => {
    const perTenK = (amount / 10000);
    return [
      { name: "Klassisch", value: Math.round((mix.classic / 100) * perTenK * INTENSITIES.classic), color: "#8B5E3C" },
      { name: "ESG-Screened", value: Math.round((mix.esg / 100) * perTenK * INTENSITIES.esg), color: "#7A8A5C" },
      { name: "Paris-Aligned", value: Math.round((mix.paris / 100) * perTenK * INTENSITIES.paris), color: "#3E5743" },
      { name: "Impact", value: Math.round((mix.impact / 100) * perTenK * INTENSITIES.impact), color: "#2C3F30" },
    ];
  }, [amount, mix]);

  const totalCO2 = chartData.reduce((a, b) => a + b.value, 0);

  // Baseline: all classic
  const baseline = Math.round((amount / 10000) * INTENSITIES.classic);
  const savings = baseline - totalCO2;

  return (
    <div className="border border-neutral-200 bg-white p-8 md:p-10" data-testid="co2-calculator">
      <div className="overline mb-3">Rechner · CO₂-Fußabdruck</div>
      <h3 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2">CO₂-Fußabdruck-Portfolio-Rechner</h3>
      <p className="text-neutral-600 mb-8 max-w-2xl">
        Vergleichen Sie die geschätzten CO₂-Emissionen Ihres Depots je nach Anteil klassischer,
        ESG-, Paris-Aligned- und Impact-Bausteine. Werte sind illustrative Branchendurchschnitte.
      </p>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-neutral-700">Depotvolumen</label>
              <span className="font-mono text-sm text-brand-primary">{amount.toLocaleString("de-DE")} €</span>
            </div>
            <Slider value={[amount]} min={1000} max={500000} step={1000} onValueChange={(v) => setAmount(v[0])} data-testid="co2-slider-amount" />
          </div>

          {[
            { k: "classic", label: "Klassische ETFs / Fonds" },
            { k: "esg", label: "ESG-Screened" },
            { k: "paris", label: "Paris-Aligned" },
            { k: "impact", label: "Impact / Cleantech" },
          ].map((row) => (
            <div key={row.k}>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-neutral-700">{row.label}</label>
                <span className="font-mono text-sm text-brand-primary">{mix[row.k]} %</span>
              </div>
              <Slider value={[mix[row.k]]} min={0} max={100} step={5} onValueChange={(v) => setPart(row.k, v[0])} data-testid={`co2-slider-${row.k}`} />
            </div>
          ))}
          <p className={`text-xs ${total === 100 ? "text-neutral-500" : "text-destructive"}`}>
            Summe der Anteile: {total} %{total !== 100 ? " (bitte auf 100 % anpassen)" : ""}
          </p>
        </div>

        <div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Ihr Fußabdruck</div>
              <div className="font-mono text-lg text-brand-dark" data-testid="co2-total">{totalCO2.toLocaleString("de-DE")} kg</div>
            </div>
            <div className="border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Klassisch-Referenz</div>
              <div className="font-mono text-lg text-neutral-700">{baseline.toLocaleString("de-DE")} kg</div>
            </div>
            <div className="border border-brand-primary p-4 bg-brand-primary text-white">
              <div className="text-xs uppercase tracking-wider text-white/80 mb-1">Einsparung</div>
              <div className="font-mono text-lg" data-testid="co2-savings">{savings.toLocaleString("de-DE")} kg</div>
            </div>
          </div>

          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(1)}t` : v)} />
                <Tooltip formatter={(v) => `${v.toLocaleString("de-DE")} kg CO₂e`} />
                <Bar dataKey="value">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-6">
        Werte sind illustrative Durchschnitte (Scope 1+2, kg CO₂e p.a.). Ersetzt keine reale Portfolioanalyse.
      </p>
    </div>
  );
}
